import React from 'react';
import { inputClass, textareaClass } from './shared';
import RecordPicker from './RecordPicker';

// Renders a skill's input from its JSON Schema. Plain fields get real inputs; structured arrays (comments,
// messages, posts) are edited as JSON so no skill needs bespoke UI. The server re-validates everything.
const CONTEXT_FIELDS = ['leadId', 'contactId', 'accountId', 'campaignId', 'dealId'];
const PICKER_TYPE = { leadId: 'lead', contactId: 'contact', accountId: 'account', campaignId: 'campaign', dealId: 'deal' };
const label = (k) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

function Field({ name, schema, required, value, onChange }) {
  const isLong = schema.type === 'string' && (schema.maxLength || 0) > 1000;
  const isStringArray = schema.type === 'array' && schema.items?.type === 'string';
  const isStructured = (schema.type === 'array' && !isStringArray) || schema.type === 'object';
  let control;
  if (schema.enum) {
    control = (
      <select className={inputClass} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value="">—</option>
        {schema.enum.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (schema.type === 'integer' || schema.type === 'number') {
    control = <input type="number" className={inputClass} min={schema.minimum} max={schema.maximum} value={value ?? schema.default ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />;
  } else if (isStringArray) {
    control = <textarea className={textareaClass} placeholder="One per line" value={(value || []).join('\n')} onChange={(e) => onChange(e.target.value ? e.target.value.split('\n') : undefined)} />;
  } else if (isStructured) {
    control = <JsonField schema={schema} value={value} onChange={onChange} />;
  } else if (isLong || ['topic', 'draft', 'relevance', 'brief', 'content'].includes(name)) {
    control = <textarea className={textareaClass} maxLength={schema.maxLength} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} />;
  } else {
    control = <input className={inputClass} maxLength={schema.maxLength} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} />;
  }
  return (
    <label className="block space-y-1">
      <span className="text-[12px] font-semibold text-[#444]">{label(name)}{required && <span className="text-[#BA0517]"> *</span>}</span>
      {schema.description && <span className="block text-[11px] text-[#706E6B]">{schema.description}</span>}
      {control}
    </label>
  );
}

function JsonField({ schema, value, onChange }) {
  const [text, setText] = React.useState(value ? JSON.stringify(value, null, 2) : '');
  const [bad, setBad] = React.useState(false);
  const example = schema.type === 'array' ? JSON.stringify([Object.fromEntries(Object.keys(schema.items?.properties || {}).slice(0, 3).map((k) => [k, '...']))], null, 1) : '';
  return (
    <>
      <textarea className={`${textareaClass} font-mono text-[12px]`} placeholder={example} value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) { setBad(false); onChange(undefined); return; }
          try { onChange(JSON.parse(e.target.value)); setBad(false); } catch { setBad(true); }
        }} />
      {bad && <span className="text-[11px] text-[#BA0517]">Not valid JSON yet</span>}
    </>
  );
}

export default function SchemaForm({ schema, values, onChange, requiresRecord }) {
  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  const own = Object.keys(props).filter((k) => !CONTEXT_FIELDS.includes(k) && k !== 'language' && k !== 'instructions');
  const set = (k) => (v) => {
    const next = { ...values };
    if (v === undefined) delete next[k]; else next[k] = v;
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {own.map((k) => <Field key={k} name={k} schema={props[k]} required={required.has(k)} value={values[k]} onChange={set(k)} />)}
      {props.language && <Field name="language" schema={props.language} value={values.language} onChange={set('language')} />}
      {props.instructions && <Field name="instructions" schema={props.instructions} value={values.instructions} onChange={set('instructions')} />}
      <details className="text-[12px]" open={Boolean(requiresRecord)}>
        <summary className="cursor-pointer font-semibold text-[#0176D3]">{requiresRecord ? 'Select the CRM record (required)' : 'Use CRM records as context (optional)'}</summary>
        <p className="text-[11px] text-[#706E6B] my-2">Only records you can already see are used, and only a few non-contact fields (e.g. name, status) are shared with the AI.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CONTEXT_FIELDS.filter((k) => props[k]).map((k) => (
            <label key={k} className="block space-y-1">
              <span className="text-[12px] font-semibold text-[#444]">{label(PICKER_TYPE[k])}</span>
              <RecordPicker type={PICKER_TYPE[k]} value={values[k]} onChange={set(k)} />
            </label>
          ))}
        </div>
      </details>
    </div>
  );
}
